import React, { useState, useContext, useEffect } from "react";
import SharedDataContext from "../../_helper/SharedData";
import { getAllCarTypes } from "../../Services/Shared.service";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Switch,
  Box,
  Skeleton,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Pagination,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmationModal from "./ConfirmationModal";
import moment from "moment";

const OfferCards = ({
  tableData,
  onStatusChange,
  onEditClick,
  onDeleteClick,
  isLoading,
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const { cityData } = useContext(SharedDataContext);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalType, setConfirmModalType] = useState(null); // 'status' | 'delete'
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [carTypesMap, setCarTypesMap] = useState({});

  useEffect(() => {
    getAllCarTypes({ page: 0, size: 30 })
      .then((res) => {
        if (res.data && res.data.content) {
          const map = {};
          res.data.content.forEach((carType) => {
            map[carType.id] = carType.name;
          });
          setCarTypesMap(map);
        }
      })
      .catch((err) => {
        console.error("Error fetching car types for mapping:", err);
      });
  }, []);

  const getCarTypeNames = (offer) => {
    try {
      const triggerCond = offer.conditions?.find(
        (c) => c.conditionType === "TRIGGER"
      );
      if (triggerCond) {
        const parsed =
          typeof triggerCond.conditionJson === "string"
            ? JSON.parse(triggerCond.conditionJson)
            : triggerCond.conditionJson;
        const carTypeIds = parsed.car_type;
        if (Array.isArray(carTypeIds) && carTypeIds.length > 0) {
          return carTypeIds
            .map((id) => carTypesMap[id] || `${id}`)
            .join(", ");
        }
      
      }
      return "—";
    } catch (err) {
      console.error("Error parsing car types from offer:", err);
      return "—";
    }
  };

  // Menu anchor state
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOfferId, setMenuOfferId] = useState(null);

  const handleMenuOpen = (event, offer) => {
    setAnchorEl(event.currentTarget);
    setMenuOfferId(offer.id);
    setSelectedOffer(offer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuOfferId(null);
  };

  const handleEditFromMenu = () => {
    if (selectedOffer) {
      onEditClick(selectedOffer);
    }
    handleMenuClose();
  };

  const handleDeleteFromMenu = () => {
    handleMenuClose();
    setConfirmModalType("delete");
    setConfirmModalOpen(true);
  };

  const handleSwitchChange = (e, offer) => {
    e.preventDefault();
    setSelectedOffer(offer);
    setConfirmModalType("status");
    setConfirmModalOpen(true);
  };

  const handleConfirm = () => {
    if (confirmModalType === "status" && selectedOffer) {
      onStatusChange(selectedOffer);
    } else if (confirmModalType === "delete" && selectedOffer) {
      onDeleteClick(selectedOffer.id);
    }
    setConfirmModalOpen(false);
    setSelectedOffer(null);
    setConfirmModalType(null);
  };

  const handleModalClose = () => {
    setConfirmModalOpen(false);
    setSelectedOffer(null);
    setConfirmModalType(null);
  };

  if (isLoading) {
    return (
      <Grid container spacing={3}>
        {[1, 2, 3, 4, 5, 6].map((item) => (
          <Grid item xs={12} md={6} key={item}>
            <Card
              sx={{
                borderRadius: "16px",
                border: "1px solid #e0e0e0",
                minHeight: "180px",
                boxShadow: "none",
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  mb={2}
                >
                  <Skeleton variant="text" width={150} height={32} />
                  <Box display="flex" gap={1}>
                    <Skeleton
                      variant="rectangular"
                      width={60}
                      height={24}
                      sx={{ borderRadius: 1 }}
                    />
                    <Skeleton variant="circular" width={30} height={20} />
                  </Box>
                </Box>
                <Box display="flex" justifyContent="space-between" mt={2}>
                  <Box>
                    <Skeleton variant="text" width={80} height={20} />
                    <Skeleton variant="text" width={120} height={24} />
                  </Box>
                  <Box textAlign="right">
                    <Skeleton variant="text" width={80} height={20} />
                    <Skeleton variant="text" width={120} height={24} />
                  </Box>
                </Box>
                <Box mt={2}>
                  <Skeleton variant="text" width={80} height={20} />
                  <Skeleton variant="text" width={100} height={24} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <>
      <Grid container spacing={3}>
        {tableData && tableData.length > 0 ? (
          tableData.map((offer, index) => (
            <Grid item xs={12} md={6} key={offer.id || index}>
              <Card
                sx={{
                  borderRadius: "16px",
                  border: offer.isActive
                    ? "1px solid #41CC7D"
                    : "1px solid #e0e0e0",
                  boxShadow: offer.isActive
                    ? "0 4px 12px rgba(65, 204, 125, 0.15)"
                    : "0 2px 4px rgba(0,0,0,0.05)",
                  transition: "all 0.3s ease",
                }}
              >
                <CardContent>
                  {/* Header: Offer Code + Status + Menu */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={1.5}
                  >
                    {/* Offer Code */}
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        border: "1px dashed",
                        borderColor: offer.isActive ? "#41CC7D" : "#ccc",
                        borderRadius: "8px",
                        backgroundColor: offer.isActive
                          ? "rgba(65, 204, 125, 0.08)"
                          : "#f4f6f8",
                        padding: "6px 12px",
                        mt: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "'Roboto Mono', monospace",
                          fontWeight: 700,
                          fontSize: "1.1rem",
                          color: offer.isActive ? "#2c3e50" : "#999",
                          letterSpacing: "1px",
                          lineHeight: 1,
                        }}
                      >
                        {offer.offerCode || "—"}
                      </Typography>
                    </Box>

                    {/* Status & Menu */}
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: offer.isActive ? "#41CC7D" : "#95a5a6",
                          fontSize: "0.8rem",
                        }}
                      >
                        {offer.isActive ? "Active" : "Inactive"}
                      </Typography>
                      <Switch
                        checked={offer.isActive}
                        onChange={(e) => handleSwitchChange(e, offer)}
                        color="success"
                        size="small"
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, offer)}
                        sx={{ color: "text.secondary", ml: -0.5 }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Offer Name */}
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: offer.isActive ? "#444" : "#999",
                      fontSize: "1rem",
                      mb: 2,
                      lineHeight: 1.3,
                    }}
                  >
                    {offer.offerName || offer.name}
                  </Typography>

                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Valid From
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {moment(offer.validFrom).format("MMM DD, YYYY h:mm A")}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Valid Upto
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {moment(offer.validTo || offer.validUpto).format(
                          "MMM DD, YYYY h:mm A",
                        )}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" justifyContent="space-between" mt={2}>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Service City
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {offer.serviceCityName ||
                          (() => {
                            try {
                              const triggerCond = offer.conditions?.find(
                                (c) => c.conditionType === "TRIGGER",
                              );
                              let cityId;
                              if (triggerCond) {
                                const parsed =
                                  typeof triggerCond.conditionJson === "string"
                                    ? JSON.parse(triggerCond.conditionJson)
                                    : triggerCond.conditionJson;
                                cityId = Array.isArray(parsed.service_city)
                                  ? parsed.service_city[0]
                                  : parsed.service_city;
                              } else {
                                cityId =
                                  offer.triggerCondition?.service_city ||
                                  offer.serviceCityId;
                              }
                              const foundCity = cityData?.find(
                                (c) => String(c.id) === String(cityId),
                              );
                              return foundCity ? foundCity.label : cityId || "N/A";
                            } catch {
                              return offer.serviceCityId || "N/A";
                            }
                          })()}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Car Type
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {getCarTypeNames(offer)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" justifyContent="flex-start" mt={2}>
                    <Box textAlign="left">
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Offer
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        color="success.main"
                      >
                        {(() => {
                          if (!offer.rewardAmount) return "N/A";
                          if (offer.rewardType === "P")
                            return `${offer.rewardAmount}%`;
                          if (offer.rewardType === "F")
                            return `₹${offer.rewardAmount}`;
                          return String(offer.rewardAmount);
                        })()}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: "16px",
                border: "1px solid #e0e0e0",
                boxShadow: "none",
                minHeight: "200px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#fff",
              }}
            >
              <CardContent>
                <Typography color="text.secondary" variant="h6">
                  No Offers found
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3} mb={2}>
          <Pagination
            count={totalPages}
            page={currentPage + 1}
            onChange={onPageChange}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* 3-dot Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            borderRadius: "10px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            minWidth: "140px",
          },
        }}
      >
        <MenuItem onClick={handleEditFromMenu}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteFromMenu} sx={{ color: "error.main" }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Confirmation Modal (for both status change and delete) */}
      <ConfirmationModal
        isOpen={confirmModalOpen}
        toggler={handleModalClose}
        onConfirm={handleConfirm}
        title={
          confirmModalType === "delete"
            ? "Confirm Delete"
            : "Confirm Status Change"
        }
        message={
          confirmModalType === "delete"
            ? "Are you sure you want to delete this offer?"
            : !selectedOffer?.isActive && moment(selectedOffer?.validTo).isBefore(moment())
              ? (
                  <span style={{ display: 'block' }}>
                    <span style={{ color: '#dc3545', fontWeight: 'bold' }}>Warning: Expired Offer</span> 
                    <br /><br />
                    This offer's validity period has ended. Even if you activate it, it will not be applied to any bookings because the 'Valid Upto' date is in the past.
                    <br /><br />
                    Are you sure you want to activate it anyway?
                  </span>
                )
            : `Are you sure you want to ${selectedOffer?.isActive ? "deactivate" : "activate"} this offer?`
        }
      />
    </>
  );
};

export default OfferCards;
